import { motion } from 'framer-motion';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import Header from '@/common/header';
import { outfit } from '@/public/assets/fonts/Outfit';

import { CreateAccountModal } from '../../create-account-modal';
import { Footer } from '../../footer';
import { Games } from '../../games';
import { PingCounter } from '../../shared/ping-counter';
import type { DefaultTrialProps } from '../../trial-layout-switcher';
import { ArenaBg } from './components/backgrounds';
import { ARSENAL_KEYS, CHAMPIONS, COMMUNITY_STATS_KEYS, DARK_BG, DEEP_PURPLE, EASE_OUT, FAQ_KEYS, MUTED_GRAY, NOPING_GREEN, PURPLE, SILVER, TEXT_LIGHT } from './components/constants';
import { ArsenalCard, ChampionCard, PingComparison, TournamentBracket } from './components/interactive';
import { Section, SectionTitle } from './components/ui-helpers';

function FaqItem({ rule, question, answer }: { rule: string; question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className='border-b border-white/10'>
      <button type='button' className='flex w-full items-center gap-4 py-5 pe-4 text-start' onClick={() => setIsOpen(!isOpen)}>
        <span className='shrink-0 rounded px-2 py-1 text-[10px] font-bold uppercase tracking-widest' style={{ color: NOPING_GREEN, background: `${NOPING_GREEN}10`, border: `1px solid ${NOPING_GREEN}20`, fontFamily: 'var(--font-bai)' }}>
          {rule}
        </span>
        <span className='text-base font-semibold text-white' style={{ fontFamily: 'var(--font-bai)' }}>
          {question}
        </span>
        <svg className={`ms-auto size-4 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} viewBox='0 0 24 24' fill='none' stroke={NOPING_GREEN} strokeWidth={2} strokeLinecap='round' strokeLinejoin='round'>
          <polyline points='6 9 12 15 18 9' />
        </svg>
      </button>
      <div className={`grid transition-all duration-200 ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className='overflow-hidden'>
          <p className='pb-5 ps-[72px] text-sm leading-relaxed' style={{ color: SILVER, fontFamily: 'var(--font-outfit)' }}>
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
}

interface GreenPillProps extends DefaultTrialProps {}

export function GreenPill({ daysTrial, trialConfigId, trialPageId, isDisabledButton = false }: GreenPillProps) {
  const { t } = useTranslation(['components/trial/lp/v3c']);
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <div className={`${outfit.variable} relative min-h-screen selection:bg-[#93FF18]/30 selection:text-white`} style={{ background: DARK_BG, color: TEXT_LIGHT, '--font-bai': "'Bai Jamjuree', sans-serif" } as React.CSSProperties}>
      <Header variant='primary' />

      {/* SECTION 1 - HERO */}
      <section className='relative flex min-h-screen flex-col items-center justify-center overflow-hidden pt-16'>
        <ArenaBg />

        <div className='relative z-10 mx-auto max-w-5xl px-4 text-center sm:px-6'>
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className='mb-8 inline-flex items-center gap-2 rounded-full border border-[#93FF18]/20 bg-[#93FF18]/5 px-4 py-1.5 text-sm font-medium' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-outfit)' }}>
            <span className='size-2 animate-pulse rounded-full bg-[#93FF18]' />
            {t('hero.badge')}
          </motion.div>

          <motion.h1 className='mb-6 text-4xl font-bold leading-[0.95] tracking-tight sm:text-5xl md:text-6xl lg:text-7xl' style={{ fontFamily: 'var(--font-bai)' }} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
            {t('hero.title_prefix')} <span style={{ color: NOPING_GREEN }}>{t('hero.title_highlight')}</span>.
            <br />
            {t('hero.title_suffix')}{' '}
            <span className='bg-clip-text text-transparent' style={{ backgroundImage: `linear-gradient(135deg, ${SILVER}, #FFFFFF)` }}>
              {t('hero.title_millions')}
            </span>
            .
          </motion.h1>

          <motion.p className='mx-auto mb-8 max-w-2xl text-base leading-relaxed sm:text-lg md:text-xl' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.25 }}>
            {t('hero.description')}
          </motion.p>

          <motion.div className='mb-8' initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.4 }}>
            <PingCounter startPing={187} endPing={9} duration={2.5} accentColor={NOPING_GREEN} size='lg' labelOptimizing={t('ping.optimizing')} labelDone={t('ping.done')} />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}>
            <motion.button type='button' onClick={() => setModalOpen(true)} className='rounded-xl px-8 py-4 text-lg font-bold uppercase tracking-wider' style={{ background: NOPING_GREEN, color: DARK_BG, fontFamily: 'var(--font-bai)', boxShadow: `0 0 20px ${NOPING_GREEN}40` }} whileHover={{ scale: 1.05, boxShadow: `0 0 30px ${NOPING_GREEN}60` }} whileTap={{ scale: 0.95 }}>
              {t('hero.cta')}
            </motion.button>
          </motion.div>

          <div className='mt-12 md:mt-16'>
            <TournamentBracket />
          </div>
        </div>

        <div className='absolute inset-x-0 bottom-0 h-32' style={{ background: `linear-gradient(to top, ${DARK_BG}, transparent)` }} />
      </section>

      {/* SECTION 2 - CHAMPION ROSTER */}
      <Section id='champions'>
        <SectionTitle subtitle={t('champions.subtitle')} title={t('champions.title')} />
        <div className='grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8'>
          {CHAMPIONS.map((champ, i) => (
            <ChampionCard key={champ.name} name={champ.name} badge={champ.badge} game={champ.game} accent={champ.accent} quote={t(champ.quoteKey)} delay={i * 0.1} />
          ))}
        </div>
      </Section>

      {/* SECTION 3 - COMMUNITY NUMBERS */}
      <Section id='community'>
        <SectionTitle subtitle={t('community.subtitle')} title={t('community.title')} />
        <div className='grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6'>
          {COMMUNITY_STATS_KEYS.map((stat, i) => (
            <motion.div key={stat.labelKey} initial={{ opacity: 0, scale: 0.85 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.5, delay: i * 0.08, ease: EASE_OUT }} className='relative overflow-hidden rounded-xl p-6 text-center md:p-8' style={{ background: `linear-gradient(180deg, ${PURPLE}, ${DEEP_PURPLE})`, border: `1px solid ${NOPING_GREEN}15` }}>
              <div className='absolute start-2 top-2 size-4 rounded-ss border-s-2 border-t-2 border-[#93FF18]/20' />
              <div className='absolute end-2 top-2 size-4 rounded-se border-e-2 border-t-2 border-[#93FF18]/20' />
              <div className='absolute bottom-2 start-2 size-4 rounded-es border-b-2 border-s-2 border-[#93FF18]/20' />
              <div className='absolute bottom-2 end-2 size-4 rounded-ee border-b-2 border-e-2 border-[#93FF18]/20' />
              <div className='mb-2 text-2xl font-bold sm:text-3xl md:text-4xl' style={{ color: NOPING_GREEN, fontFamily: 'var(--font-bai)' }}>
                {t(stat.valueKey)}
              </div>
              <div className='text-xs font-semibold uppercase tracking-widest sm:text-sm' style={{ color: SILVER, fontFamily: 'var(--font-outfit)' }}>
                {t(stat.labelKey)}
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* SECTION 4 - ARSENAL */}
      <Section id='arsenal'>
        <SectionTitle subtitle={t('arsenal.subtitle')} title={t('arsenal.title')} description={t('arsenal.description')} />
        <div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3'>
          {ARSENAL_KEYS.map((tool, i) => (
            <ArsenalCard key={tool.nameKey} name={t(tool.nameKey)} tagline={t(tool.taglineKey)} desc={t(tool.descKey)} iconPath={tool.iconPath} secondaryPath={'secondaryPath' in tool ? tool.secondaryPath : undefined} delay={i * 0.06} />
          ))}
        </div>
      </Section>

      {/* SECTION 5 - GAMES */}
      <Section id='games'>
        <SectionTitle subtitle={t('games.subtitle')} title={t('games.title')} description={t('games.description')} />
        <Games daysTrial={daysTrial} isActivedButton={false} bgImage='/assets/images/trial/fundo_kohogs.webp' />
      </Section>

      {/* SECTION 6 - RANK UP */}
      <Section id='rank-up'>
        <SectionTitle subtitle={t('rank_up.subtitle')} title={t('rank_up.title')} titleAccent={t('rank_up.title_accent')} description={t('rank_up.description')} />
        <PingComparison />
        <div className='mt-12 text-center'>
          <motion.button type='button' onClick={() => setModalOpen(true)} className='rounded-xl px-8 py-4 text-base font-bold uppercase tracking-wider' style={{ background: NOPING_GREEN, color: DARK_BG, fontFamily: 'var(--font-bai)', boxShadow: `0 0 20px ${NOPING_GREEN}40` }} whileHover={{ scale: 1.05, boxShadow: `0 0 30px ${NOPING_GREEN}60` }} whileTap={{ scale: 0.95 }}>
            {t('rank_up.cta')}
          </motion.button>
        </div>
      </Section>

      {/* SECTION 7 - FAQ */}
      <Section id='faq'>
        <SectionTitle subtitle={t('faq.subtitle')} title={t('faq.title')} />
        <div className='mx-auto max-w-3xl'>
          {FAQ_KEYS.map((item, i) => (
            <FaqItem key={i} rule={t(item.ruleKey)} question={t(item.qKey)} answer={t(item.aKey)} />
          ))}
        </div>
      </Section>

      {/* SECTION 8 - FOOTER CTA */}
      <Section id='final-cta' className='py-24 md:py-36'>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.6, ease: EASE_OUT }} className='relative overflow-hidden rounded-2xl p-10 text-center md:p-16' style={{ background: `linear-gradient(180deg, ${PURPLE}, ${DEEP_PURPLE})` }}>
          <div className='absolute inset-0 rounded-2xl' style={{ border: `2px solid ${NOPING_GREEN}25` }} />
          <div className='absolute start-0 top-0 size-12 rounded-ss-2xl border-s-2 border-t-2' style={{ borderColor: NOPING_GREEN }} />
          <div className='absolute end-0 top-0 size-12 rounded-se-2xl border-e-2 border-t-2' style={{ borderColor: NOPING_GREEN }} />
          <div className='absolute bottom-0 start-0 size-12 rounded-es-2xl border-b-2 border-s-2' style={{ borderColor: NOPING_GREEN }} />
          <div className='absolute bottom-0 end-0 size-12 rounded-ee-2xl border-b-2 border-e-2' style={{ borderColor: NOPING_GREEN }} />
          <div className='absolute left-1/2 top-4 size-6 -translate-x-1/2 rotate-45 border border-[#93FF18]/20' />
          <div className='absolute bottom-4 left-1/2 size-6 -translate-x-1/2 rotate-45 border border-[#93FF18]/20' />
          <div className='absolute inset-x-16 top-8 h-px bg-gradient-to-r from-transparent via-[#93FF18]/15 to-transparent' />
          <div className='absolute inset-x-16 bottom-8 h-px bg-gradient-to-r from-transparent via-[#93FF18]/15 to-transparent' />

          <div className='relative z-10'>
            <span className='mb-6 block text-5xl md:text-6xl'>&#x1F3C6;</span>
            <h2 className='mb-4 text-3xl font-bold sm:text-4xl md:text-5xl' style={{ fontFamily: 'var(--font-bai)' }}>
              {t('final_cta.title')}
            </h2>
            <p className='mb-2 text-lg md:text-xl' style={{ color: SILVER, fontFamily: 'var(--font-outfit)' }}>
              {t('final_cta.subtitle')}
            </p>
            <p className='mb-10 text-sm' style={{ color: `${SILVER}99`, fontFamily: 'var(--font-outfit)' }}>
              {t('final_cta.offer')}
            </p>
            <motion.button type='button' onClick={() => setModalOpen(true)} className='rounded-xl px-8 py-4 text-lg font-bold uppercase tracking-wider' style={{ background: NOPING_GREEN, color: DARK_BG, fontFamily: 'var(--font-bai)', boxShadow: `0 0 20px ${NOPING_GREEN}40` }} whileHover={{ scale: 1.05, boxShadow: `0 0 30px ${NOPING_GREEN}60` }} whileTap={{ scale: 0.95 }}>
              {t('final_cta.cta')}
            </motion.button>
          </div>
        </motion.div>
      </Section>

      <Footer text={t('footer', { year: new Date().getFullYear() })} />

      <CreateAccountModal open={modalOpen} onClose={() => setModalOpen(false)} daysTrial={daysTrial} customTrialData={{ custom: trialConfigId, trialPageId }} isDisabledButton={isDisabledButton} formId='v3c-modal-form' />
    </div>
  );
}
